'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import NextLink from 'next/link';
import { alpha, useTheme } from '@mui/material/styles';
import { trackLandingCtaClicked } from '../../../lib/analytics';

interface CtaSectionProps {
  locale: string;
}

export function CtaSection({ locale }: CtaSectionProps) {
  const t = useTranslations('creator.serviceBot.cta');
  const theme = useTheme();

  const handleCtaClick = () => {
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    trackLandingCtaClicked('service', locale, referrer);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 10 },
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
        color: 'primary.contrastText',
      }}
    >
      <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h4"
          component="h2"
          fontWeight={700}
          sx={{ mb: 2, color: 'inherit' }}
        >
          {t('title')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 4, color: 'inherit', opacity: 0.9 }}
        >
          {t('subtitle')}
        </Typography>
        <Button
          component={NextLink}
          href={`/${locale}`}
          onClick={handleCtaClick}
          variant="contained"
          size="large"
          sx={{
            py: 1.5,
            px: 5,
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 3,
            textTransform: 'none',
            bgcolor: 'common.white',
            color: 'primary.main',
            minWidth: { xs: '100%', sm: 'auto' },
            minHeight: 48,
            '&:hover': {
              bgcolor: alpha(theme.palette.common.white, 0.9),
            },
          }}
        >
          {t('button')}
        </Button>
      </Container>
    </Box>
  );
}
